import cancelSvg from '@/shared/icons/cancel.svg';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Dialog } from '@base-ui/react/dialog';
import { getUniqueNumber } from '@/modules/get-unique-number';

import { zModalsWr } from '@/shared/constants/zIdxs';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useIsMobile } from '@/modules/use-window-width';

// Scroll-neutral modal stack. Each modal's wrapper is its own fixed
// full-viewport scroll container, so opening/closing modals never moves
// window scroll — body-scroll-lock only disables user scrolling of the
// page (and reserves the scrollbar gap), keeping the offset intact.
// Stacked modals: only the top one is visible; lower ones hide via
// `visibility` (not `display`, which would destroy the box and lose the
// wrapper's scrollTop), so each modal's scroll position survives
// modal-over-modal round trips by construction — no save/restore.
// The stack is app-owned: Base UI links dialogs only when one renders inside
// another in the React tree, and modals here open from unrelated trees
// (providers, the wallet guard, pages).
const modalIdsAtom = atom<number[]>([]);

// For UI that must yield to modals entirely (onboarding coach-marks unmount
// while any modal is up) instead of fighting them with z-index.
export function useAnyModalOpen(): boolean {
  return useAtomValue(modalIdsAtom).length > 0;
}

export default function Modal({
  closeModal,
  children,
  fullScreen,
  withoutCross = false,
  closeOnOverlayClick = true,
  sheet = false,
  drawer = false,
  ariaLabel,
  open = true,
  onClosed,
}: {
  closeModal?: (() => void) | undefined;
  children: ReactNode;
  fullScreen?: boolean;
  withoutCross?: boolean;
  closeOnOverlayClick?: boolean;
  sheet?: boolean;
  // A right-side panel the full viewport height (desktop surfaces); the
  // card fills the wrapper and its content owns any inner scrolling.
  drawer?: boolean;
  ariaLabel?: string;
  // `open` false closes the dialog without unmounting this component: the
  // drawer and the sheet slide out first, and `onClosed` fires once the
  // exit transition has ended — the moment a parent may unmount. The other
  // layouts have no transition and close at once.
  open?: boolean;
  onClosed?: (() => void) | undefined;
}) {
  const modalIds = useAtomValue(modalIdsAtom);
  const setModalIds = useSetAtom(modalIdsAtom);
  const isMobile = useIsMobile();
  const isSheet = sheet && isMobile;
  const resolvedFullScreen =
    isSheet || drawer ? false
    : typeof fullScreen === 'boolean' ? fullScreen
    : isMobile;

  const [id] = useState(getUniqueNumber);
  // Element state, not refs: Base UI mounts the portal content one render
  // after the dialog itself, so a ref read in this component's mount effect
  // would still be empty.
  const [wr, setWr] = useState<HTMLDivElement | null>(null);
  const [card, setCard] = useState<HTMLDivElement | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const cardIsScroller = resolvedFullScreen || isSheet;
  // Registered while open: a closing modal leaves the stack at once, so a
  // modal under it is exposed while this one slides out.
  useLayoutEffect(() => {
    if (!open) return undefined;
    setModalIds((ids) => [...ids, id]);
    return () => {
      setModalIds((ids) => ids.filter((x) => x !== id));
    };
  }, [id, setModalIds, open]);
  // Lock target must be the element that actually scrolls (body-scroll-lock
  // allows scrolling inside its target and blocks the page): the wrapper,
  // except when the card is the scroller. No allowTouchMove — a blanket allow
  // over a viewport-covering wrapper would disable the iOS lock entirely.
  // Keyed on the scroller CHOICE, not mount: crossing 767px while open (e.g.
  // rotation) re-renders the sheet/fullscreen layout live, so the lock must
  // re-target the new scroller or iOS touch scrolling inside the modal dies.
  useLayoutEffect(() => {
    if (wr == null) return undefined;
    const scroller = (cardIsScroller ? card : null) ?? wr;
    disableBodyScroll(scroller, { reserveScrollBarGap: true });
    return () => {
      enableBodyScroll(scroller);
    };
  }, [wr, card, cardIsScroller]);

  const isOverlayPressedRef = useRef<boolean>(false);
  const isTop = modalIds.slice(-1)[0] === id;
  // The drawer slides in from the right edge and the sheet up from the
  // bottom, the dim fading with them; the viewport stops scrolling while
  // the card is translated, since a transformed card extends its scroll
  // range.
  const slides = drawer || isSheet;

  return (
    // trap-focus, not modal: a modal Base UI dialog would add its own page
    // scroll lock, which on iOS only sets overflow:hidden and stops holding
    // once Safari's toolbar has collapsed — body-scroll-lock stays the one
    // lock. Pointer dismissal stays the overlay handler's below: Base UI
    // would also count presses in portals layered over the modal (the
    // RainbowKit connect modal, the image lightbox) and on the wrapper's
    // scrollbar.
    <Dialog.Root
      open={open}
      modal="trap-focus"
      disablePointerDismissal
      onOpenChange={(nowOpen) => {
        if (!nowOpen) closeModal?.();
      }}
      onOpenChangeComplete={(nowOpen) => {
        if (!nowOpen) onClosed?.();
      }}
    >
      <Dialog.Portal>
        <Dialog.Viewport
          ref={setWr}
          tabIndex={-1}
          // The dim must be pointer-events-none: it's a positioned (fixed) box, so
          // it would otherwise be the hit-test target for every click and wheel on
          // the empty area (pseudo hits target the wrapper, and wheel over a fixed
          // box chains to the locked viewport, not the wrapper's scroller).
          className={`fixed inset-0 overflow-x-hidden overflow-y-auto outline-none before:pointer-events-none before:fixed before:inset-0 before:bg-background-secondary before:opacity-70 before:content-['']${
            slides ?
              ' before:transition-opacity before:duration-300 data-ending-style:overflow-hidden data-ending-style:before:opacity-0 data-starting-style:overflow-hidden data-starting-style:before:opacity-0'
            : ''
          }`}
          style={{
            zIndex: zModalsWr,
            // A closing modal has left the stack but stays visible for its
            // exit.
            visibility: open && !isTop ? 'hidden' : undefined,
          }}
          // Shield presses that target the wrapper itself (its scrollbar) from
          // React parents, matching the inner div's shield for everything else.
          onMouseDown={(event) => event.stopPropagation()}
        >
          <div
            className={`flex min-h-full w-full ${
              drawer ? 'items-stretch justify-end'
              : isSheet ? 'items-end justify-center'
              : 'items-center justify-center'
            }`}
            onMouseDown={(event) => {
              event.stopPropagation();
              const { target, currentTarget } = event;
              isOverlayPressedRef.current = target === currentTarget;
            }}
            onMouseUp={({ target, currentTarget }) => {
              if (target !== currentTarget) {
                return;
              }
              if (!isOverlayPressedRef.current) {
                return;
              }
              if (!closeOnOverlayClick) {
                return;
              }
              closeModal?.();
            }}
          >
            <Dialog.Popup
              ref={(node) => {
                cardRef.current = node;
                setCard(node);
              }}
              // The card itself, not its first tabbable: keyboard scrolling
              // follows the focused element's scroll chain, and a focused
              // input would raise the phone keyboard. An autoFocus child
              // keeps its focus — Base UI leaves focus that is already inside.
              initialFocus={cardRef}
              aria-label={ariaLabel}
              className={`relative z-1 flex max-w-full flex-col overflow-hidden bg-background-secondary shadow-[0px_4px_18px_rgba(0,0,0,0.25)] outline-none ${
                resolvedFullScreen ?
                  'fixed top-0 right-0 bottom-0 left-0 overflow-auto rounded-lg'
                : isSheet ?
                  'max-h-[92svh] w-full overflow-y-auto rounded-t-lg transition-transform duration-300 ease-out data-ending-style:translate-y-full data-starting-style:translate-y-full'
                : drawer ?
                  'h-svh rounded-l-lg transition-transform duration-300 ease-out data-ending-style:translate-x-full data-starting-style:translate-x-full'
                : 'rounded-lg'
              }`}
            >
              {closeModal && !withoutCross && (
                <CloseCross
                  fullScreen={resolvedFullScreen}
                  onClick={() => closeModal()}
                />
              )}
              {children}
            </Dialog.Popup>
          </div>
        </Dialog.Viewport>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function CloseCross({
  fullScreen,
  onClick,
}: {
  fullScreen: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label="Close"
      className={`absolute z-1 btn-reset ${
        fullScreen ?
          'top-4xl right-9 h-2xl w-2xl max-[374px]:top-3xl max-[374px]:right-3xl'
        : 'top-l right-l h-3xl w-3xl'
      }`}
      style={{
        backgroundColor: 'var(--gray-2)',
        mask: `url(${JSON.stringify(cancelSvg)}) no-repeat center`,
        maskSize: fullScreen ? '24px 24px' : '32px 32px',
        WebkitMask: `url(${JSON.stringify(cancelSvg)}) no-repeat center`,
        WebkitMaskSize: fullScreen ? '24px 24px' : '32px 32px',
      }}
      onClick={onClick}
    />
  );
}
